import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, GripVertical, Save, AlertCircle, CheckCircle, FileText, ListChecks } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExamBuilder = ({ lesson, courseId, existingExam, onClose, onSaved }) => {
  const isEditMode = Boolean(existingExam);
  const [examTitle, setExamTitle] = useState(existingExam?.title || `Assessment: ${lesson?.lesson_title || lesson?.title || 'Lesson Exam'}`);
  const [passingScore, setPassingScore] = useState(existingExam?.passing_score ?? 70);
  const [allowRetakes, setAllowRetakes] = useState(existingExam?.allow_retakes ?? true);
  const [questions, setQuestions] = useState(() => {
    if (!existingExam?.questions) return [];
    // Normalise so each question has stable keys + filled options arrays
    return existingExam.questions.map((q, idx) => ({
      id: q.id || `q_${Date.now()}_${idx}`,
      question_type: q.question_type || 'multiple_choice',
      question_text: q.question_text || '',
      options: Array.isArray(q.options) && q.options.length > 0
        ? q.options.map(o => ({ label: o.label, text: o.text || '' }))
        : (q.question_type === 'true_false'
          ? [{ label: 'True', text: 'True' }, { label: 'False', text: 'False' }]
          : q.question_type === 'short_answer'
          ? null
          : [
              { label: 'A', text: '' },
              { label: 'B', text: '' },
              { label: 'C', text: '' },
              { label: 'D', text: '' }
            ]),
      correct_answer: q.correct_answer ?? '',
      points: q.points || 1,
      order: q.order ?? idx + 1,
      lesson_timestamp_seconds: typeof q.lesson_timestamp_seconds === 'number' ? q.lesson_timestamp_seconds : null,
    }));
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Refresh state when existingExam changes (e.g., re-opening with different exam)
  useEffect(() => {
    if (existingExam) {
      setExamTitle(existingExam.title || '');
      setPassingScore(existingExam.passing_score ?? 70);
      setAllowRetakes(existingExam.allow_retakes ?? true);
    }
  }, [existingExam?.id]);

  const addQuestion = (type = 'multiple_choice') => {
    const newQuestion = {
      id: `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      question_type: type,
      question_text: '',
      options: type === 'multiple_choice' ? [
        { label: 'A', text: '' },
        { label: 'B', text: '' },
        { label: 'C', text: '' },
        { label: 'D', text: '' }
      ] : type === 'true_false' ? [
        { label: 'True', text: 'True' },
        { label: 'False', text: 'False' }
      ] : null,
      correct_answer: type === 'true_false' ? 'True' : '',
      points: 1,
      lesson_timestamp_seconds: null,
    };
    setQuestions([...questions, newQuestion]);
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    setQuestions(updated);
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    const updated = [...questions];
    updated[questionIndex].options[optionIndex].text = value;
    setQuestions(updated);
  };

  const removeQuestion = (index) => {
    const updated = questions.filter((_, i) => i !== index);
    setQuestions(updated);
  };

  const moveQuestion = (index, direction) => {
    if ((direction === -1 && index === 0) || (direction === 1 && index === questions.length - 1)) return;
    const updated = [...questions];
    const temp = updated[index];
    updated[index] = updated[index + direction];
    updated[index + direction] = temp;
    setQuestions(updated);
  };

  // Helpers for the optional "review at" timestamp linking each question to a
  // specific point in the lesson video.
  const formatTimestamp = (secs) => {
    if (secs === null || secs === undefined || secs === '' || Number.isNaN(Number(secs))) return '';
    const total = Math.max(0, Math.floor(Number(secs)));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
  };
  const parseTimestamp = (raw) => {
    if (!raw || !raw.trim()) return null;
    const parts = raw.trim().split(':').map((p) => parseInt(p, 10));
    if (parts.some((p) => Number.isNaN(p) || p < 0)) return null;
    if (parts.length === 1) return parts[0];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    return null;
  };

  const validateExam = () => {
    if (!examTitle.trim()) {
      toast.error('Please enter an exam title');
      return false;
    }
    if (questions.length === 0) {
      toast.error('Please add at least one question');
      return false;
    }
    let mcqWithoutAnswer = 0;
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.question_text.trim()) {
        toast.error(`Question ${i + 1} is empty — please fill it in or delete it`);
        return false;
      }
      if (q.question_type === 'multiple_choice') {
        const filledOptions = (q.options || []).filter(o => (o.text || '').trim());
        if (filledOptions.length < 2) {
          toast.error(`Question ${i + 1} needs at least 2 options`);
          return false;
        }
        // correct_answer is optional — some MCQs are intentional self-reflection
        // prompts (e.g. "Which area needs the most work?") that the mentor grades
        // by hand. Backend accepts null, so we just warn the admin once.
        if (!q.correct_answer) mcqWithoutAnswer += 1;
      }
    }
    if (mcqWithoutAnswer > 0) {
      toast.warning(
        `Saved with ${mcqWithoutAnswer} reflection question${mcqWithoutAnswer === 1 ? '' : 's'} that have no correct answer (will be reviewed manually).`,
        { duration: 4000 }
      );
    }
    return true;
  };

  const saveExam = async () => {
    if (!validateExam()) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // Prepare questions with order
      const preparedQuestions = questions.map((q, idx) => ({
        ...q,
        order: idx + 1,
        points: q.points || 1
      }));

      const examData = {
        lesson_id: lesson.lesson_id || lesson.id,
        course_id: courseId || existingExam?.course_id || '',
        title: examTitle,
        description: existingExam?.description || `Interactive assessment for ${lesson.lesson_title || lesson.title || ''}`,
        questions: preparedQuestions,
        passing_score: passingScore,
        allow_retakes: allowRetakes,
        is_published: true
      };

      console.log('[ExamBuilder] Saving exam:', { url: `${API_URL}/api/exams/create-or-update`, body: examData });

      const response = await fetch(`${API_URL}/api/exams/create-or-update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(examData)
      });

      let data = {};
      try {
        data = await response.json();
      } catch (parseErr) {
        console.error('[ExamBuilder] Failed to parse server response:', parseErr);
      }

      if (response.ok) {
        toast.success(`Exam ${data.action || 'saved'}! ${data.question_count ?? preparedQuestions.length} questions saved.`);
        onSaved && onSaved();
        onClose();
      } else {
        console.error('[ExamBuilder] Save failed:', response.status, data);
        const detail = (typeof data?.detail === 'string' ? data.detail : null)
          || (Array.isArray(data?.detail) ? data.detail.map(d => d.msg || JSON.stringify(d)).join('; ') : null);
        toast.error(detail || `Failed to save exam (HTTP ${response.status})`);
      }
    } catch (error) {
      console.error('[ExamBuilder] Network error saving exam:', error);
      toast.error('Failed to save exam — check your connection');
    } finally {
      setSaving(false);
    }
  };

  const deleteExam = async () => {
    if (!existingExam?.id) return;
    if (!window.confirm(`Delete the exam "${existingExam.title}"?\n\nThis will also remove all student attempts and cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_URL}/api/exams/${existingExam.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Exam deleted');
        onSaved && onSaved();
        onClose();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to delete exam');
      }
    } catch (e) {
      console.error('Delete exam failed:', e);
      toast.error('Failed to delete exam');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-xl">
          <div>
            <h2 className="text-xl font-bold">Create Exam</h2>
            <p className="text-purple-100 text-sm">For: {lesson?.lesson_title}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Exam Settings */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              Exam Settings
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Exam Title</label>
                <input
                  type="text"
                  value={examTitle}
                  onChange={(e) => setExamTitle(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter exam title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Passing Score (%)</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={passingScore}
                  onChange={(e) => setPassingScore(parseInt(e.target.value) || 70)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="allowRetakes"
                checked={allowRetakes}
                onChange={(e) => setAllowRetakes(e.target.checked)}
                className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
              />
              <label htmlFor="allowRetakes" className="text-sm text-gray-700">Allow students to retake this exam</label>
            </div>
          </div>

          {/* Questions */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <ListChecks className="w-5 h-5 text-purple-600" />
                Questions ({questions.length})
              </h3>
              
              <div className="flex gap-2">
                <button
                  onClick={() => addQuestion('multiple_choice')}
                  className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-200 transition-colors flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Multiple Choice
                </button>
                <button
                  onClick={() => addQuestion('true_false')}
                  className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  True/False
                </button>
                <button
                  onClick={() => addQuestion('short_answer')}
                  className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 transition-colors flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Short Answer
                </button>
              </div>
            </div>

            {questions.length === 0 ? (
              <div className="bg-gray-50 rounded-xl p-8 text-center">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No questions added yet</p>
                <p className="text-gray-500 text-sm mt-1">Click the buttons above to add questions</p>
              </div>
            ) : (
              <div className="space-y-4">
                {questions.map((question, qIndex) => (
                  <div key={question.id} className="bg-white border rounded-xl p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                      {/* Drag handle and number */}
                      <div className="flex flex-col items-center gap-1 pt-2">
                        <span className="w-7 h-7 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-sm font-bold">
                          {qIndex + 1}
                        </span>
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => moveQuestion(qIndex, -1)}
                            disabled={qIndex === 0}
                            className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                            title="Move up"
                          >
                            ▲
                          </button>
                          <button
                            onClick={() => moveQuestion(qIndex, 1)}
                            disabled={qIndex === questions.length - 1}
                            className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                            title="Move down"
                          >
                            ▼
                          </button>
                        </div>
                      </div>

                      {/* Question content */}
                      <div className="flex-1 space-y-3">
                        {/* Question type badge */}
                        <div className="flex items-center justify-between">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            question.question_type === 'multiple_choice' ? 'bg-purple-100 text-purple-700' :
                            question.question_type === 'true_false' ? 'bg-blue-100 text-blue-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {question.question_type === 'multiple_choice' ? 'Multiple Choice' :
                             question.question_type === 'true_false' ? 'True/False' : 'Short Answer'}
                          </span>
                          <button
                            onClick={() => removeQuestion(qIndex)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete question"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Question text */}
                        <textarea
                          value={question.question_text}
                          onChange={(e) => updateQuestion(qIndex, 'question_text', e.target.value)}
                          placeholder="Enter your question here..."
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                          rows={2}
                        />

                        {/* Lesson timestamp link (optional) */}
                        <div className="flex items-center gap-2 text-sm">
                          <label
                            htmlFor={`ts_${question.id}`}
                            className="text-gray-600 font-medium whitespace-nowrap"
                          >
                            Review at (mm:ss):
                          </label>
                          <input
                            id={`ts_${question.id}`}
                            type="text"
                            inputMode="numeric"
                            placeholder="e.g. 2:34"
                            value={formatTimestamp(question.lesson_timestamp_seconds)}
                            onChange={(e) =>
                              updateQuestion(qIndex, 'lesson_timestamp_seconds', parseTimestamp(e.target.value))
                            }
                            className="w-28 px-2 py-1 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            data-testid={`question-timestamp-${qIndex}`}
                          />
                          <span className="text-xs text-gray-500">
                            Optional — sends students back to this point in the lesson video when they miss this question.
                          </span>
                        </div>

                        {/* Options for Multiple Choice */}
                        {question.question_type === 'multiple_choice' && (
                          <div className="space-y-2">
                            <p className="text-sm text-gray-600 font-medium">Options (click radio to mark correct answer):</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {question.options.map((option, oIndex) => (
                                <div key={option.label} className="flex items-center gap-2">
                                  <input
                                    type="radio"
                                    name={`correct_${question.id}`}
                                    checked={question.correct_answer === option.label}
                                    onChange={() => updateQuestion(qIndex, 'correct_answer', option.label)}
                                    className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                                  />
                                  <span className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center text-sm font-bold text-gray-600">
                                    {option.label}
                                  </span>
                                  <input
                                    type="text"
                                    value={option.text}
                                    onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                                    placeholder={`Option ${option.label}`}
                                    className={`flex-1 px-2 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                                      question.correct_answer === option.label ? 'border-green-500 bg-green-50' : ''
                                    }`}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Options for True/False */}
                        {question.question_type === 'true_false' && (
                          <div className="space-y-2">
                            <p className="text-sm text-gray-600 font-medium">Correct answer:</p>
                            <div className="flex gap-4">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`tf_${question.id}`}
                                  checked={question.correct_answer === 'True'}
                                  onChange={() => updateQuestion(qIndex, 'correct_answer', 'True')}
                                  className="w-4 h-4 text-green-600 focus:ring-green-500"
                                />
                                <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
                                  question.correct_answer === 'True' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  True
                                </span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`tf_${question.id}`}
                                  checked={question.correct_answer === 'False'}
                                  onChange={() => updateQuestion(qIndex, 'correct_answer', 'False')}
                                  className="w-4 h-4 text-red-600 focus:ring-red-500"
                                />
                                <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
                                  question.correct_answer === 'False' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  False
                                </span>
                              </label>
                            </div>
                          </div>
                        )}

                        {/* Short Answer hint */}
                        {question.question_type === 'short_answer' && (
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <p className="text-sm text-blue-700">
                              <strong>Note:</strong> Short answer questions will be manually reviewed by the mentor.
                              Students will type their response in a text area.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex items-center justify-between rounded-b-xl">
          <div className="text-sm text-gray-600">
            {questions.length > 0 && (
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                {questions.length} question{questions.length !== 1 ? 's' : ''} ready
              </span>
            )}
          </div>
          <div className="flex gap-3">
            {isEditMode && (
              <button
                onClick={deleteExam}
                disabled={deleting || saving}
                className="px-4 py-2 text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                data-testid="exam-builder-delete-btn"
              >
                <Trash2 className="w-4 h-4" />
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              data-testid="exam-builder-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={saveExam}
              disabled={saving || questions.length === 0}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-purple-300 flex items-center gap-2"
              data-testid="exam-builder-save-btn"
            >
              {saving ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditMode ? 'Update Exam' : 'Save Exam'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamBuilder;
